import { useEffect, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useVisitBookmark } from "../../services/bookmarkService";
import { Form, InputGroup, Badge, Button } from "react-bootstrap";
import { Modal } from "../Modal";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { Empty } from "./Empty";
import { useSearch } from "../../services/searchService";
import { useAsk } from "../../services/askService";
import { useAccount } from "../../context/AccountContext";
import useDebounce from "../../hooks/useDebounce";
import { Icon, Title, Url } from "../Bookmarks/Item/Content";
import { CollectionBadge } from "../Bookmarks/Item/Content/CollectionBadge";
import { Loading } from "./Loading";
import { NoResults } from "./NoResults";

const SearchModal = ({ show, onHide, query, onQueryChange }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const visitBookmark = useVisitBookmark();
  const { account } = useAccount();
  const isPro = account?.plan === "pro";

  const searchInput = query;
  const debouncedSearchTerm = useDebounce(searchInput, 300);

  // Ask ("Ask me anything") is now folded into this palette.
  const { mutateAsync: ask, isPending: isAsking } = useAsk();
  const [askResult, setAskResult] = useState(null);
  const [askError, setAskError] = useState("");

  const handleAsk = async () => {
    const q = searchInput.trim();
    if (!q || isAsking) return;
    setAskError("");
    setAskResult(null);
    try {
      const res = await ask(q);
      setAskResult(res);
    } catch (e) {
      setAskError(e?.message || t("ask.error", "Couldn't answer that. Try again."));
    }
  };

  // Drop any answer when the query changes or the modal closes.
  useEffect(() => {
    setAskResult(null);
    setAskError("");
  }, [searchInput]);

  useEffect(() => {
    if (!show) {
      setAskResult(null);
      setAskError("");
    }
  }, [show]);

  // Search results
  const { data: searchResults, isPending: isSearching } = useSearch(
    debouncedSearchTerm,
    {},
    debouncedSearchTerm.length > 1
  );

  const results = searchResults?.data || {
    bookmarks: [],
    collections: [],
    tags: [],
    pagination: { total: 0 },
  };

  // Focus the input when modal opens
  useEffect(() => {
    if (show && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 100);
    }
  }, [show]);

  // Handle input changes
  const handleInputChange = (e) => {
    const value = e.target.value;
    onQueryChange(value);
  };

  // Handle form submission. For Pro users this palette asks the AI (per the
  // "Ask me anything" design); otherwise it falls back to a full search.
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!searchInput.trim()) return;
    if (isPro) {
      handleAsk();
    } else {
      navigate(`/search?q=${encodeURIComponent(searchInput.trim())}`);
      onHide();
    }
  };

  // Handle search example click
  const handleExampleClick = (query) => {
    onQueryChange(query);
    navigate(`/search?q=${encodeURIComponent(query)}`);
    onHide();
  };

  const openBookmark = useCallback((bookmark) => {
    visitBookmark.mutate(bookmark.uuid);
    // Open the URL in a new tab
    window.open(bookmark.url, "_blank", "noopener,noreferrer");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop="static"
      centered
      size="lg"
      dialogClassName="search-modal"
      restoreFocus={false}
    >
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <InputGroup className="search-modal-input p-2">
            <InputGroup.Text className="border-0 bg-transparent py-0">
              <i className="bi bi-search"></i>
            </InputGroup.Text>
            <Form.Control
              type="search"
              name="search"
              placeholder={
                isPro
                  ? t(
                      "search.askPlaceholder",
                      "Search or ask anything about your bookmarks"
                    )
                  : t("search.placeholder")
              }
              value={query}
              onChange={handleInputChange}
              ref={inputRef}
              autoFocus
              autoComplete="off"
              className="border-0 shadow-none p-0"
            />
            <InputGroup.Text className="bg-transparent border-0">
              <div className="kbd-shortcut d-none d-lg-block">
                <kbd>ESC</kbd>
              </div>
            </InputGroup.Text>
          </InputGroup>
        </Form>
        <div className="search-results-container">
          {/* Ask AI action — the "Ask me anything" flow lives here now */}
          {searchInput && isPro && (
            <button
              type="button"
              className="ask-action w-100 d-flex align-items-center gap-3 p-3 border-bottom text-start"
              onClick={handleAsk}
              disabled={isAsking}
            >
              <span className="ask-action__icon d-flex align-items-center justify-content-center">
                <i className="bi bi-stars"></i>
              </span>
              <span className="flex-grow-1 min-w-0">
                <span className="d-block fw-medium">
                  {t("ask.modal.action", "Ask AI")}
                </span>
                <span className="small text-secondary text-truncate d-block">
                  "{searchInput}"
                </span>
              </span>
              {isAsking ? (
                <span className="spinner-border spinner-border-sm text-primary"></span>
              ) : (
                <kbd className="d-none d-lg-block">↵</kbd>
              )}
            </button>
          )}

          {searchInput && !isPro && (
            <Link
              to="/settings/subscription"
              onClick={onHide}
              className="ask-action w-100 d-flex align-items-center gap-3 p-3 border-bottom text-decoration-none text-reset"
            >
              <span className="ask-action__icon d-flex align-items-center justify-content-center">
                <i className="bi bi-stars"></i>
              </span>
              <span className="flex-grow-1 min-w-0">
                <span className="d-block fw-medium">
                  {t("ask.modal.actionPro", "Ask AI about your bookmarks")}
                </span>
                <span className="small text-secondary d-block">
                  {t(
                    "ask.modal.proHint",
                    "Upgrade to Pro to ask in natural language"
                  )}
                </span>
              </span>
              <span className="badge bg-purple bg-opacity-10 text-purple">
                {t("subscription.pro", "PRO")}
              </span>
            </Link>
          )}

          {askError && (
            <div className="p-3 border-bottom small text-warning">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {askError}
            </div>
          )}

          {askResult && (
            <div className="ask-answer p-3 border-bottom">
              <div className="d-flex align-items-center gap-2 mb-2 text-primary">
                <i className="bi bi-stars"></i>
                <span className="fw-semibold small text-uppercase">
                  {t("ask.answer", "Answer")}
                </span>
              </div>
              <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                {askResult.answer}
              </p>
              {askResult.sources?.length > 0 && (
                <div className="mt-3 d-flex flex-column gap-2">
                  {askResult.sources.map((s) => (
                    <a
                      key={s.uuid}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ask-source d-flex align-items-center gap-2 text-decoration-none text-reset"
                    >
                      <span className="ask-source__n">{s.n}</span>
                      <span className="text-truncate flex-grow-1">
                        <span className="d-block text-truncate">
                          {s.title || s.url}
                        </span>
                        <span className="small text-secondary text-truncate d-block">
                          {s.domain || s.url}
                        </span>
                      </span>
                      <i className="bi bi-box-arrow-up-right text-secondary"></i>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          {searchInput && isSearching && <Loading />}

          {searchInput === "" && (
            <Empty onExampleClick={(q) => onQueryChange(q)} />
          )}

          {/* Show No results when no bookmarks or collections */}
          {searchInput &&
            !isSearching &&
            results?.bookmarks?.length === 0 &&
            results?.collections?.length === 0 && (
              <NoResults query={searchInput} />
            )}

          {/* Show search results if there are any */}
          {searchInput && !isSearching && results?.bookmarks?.length > 0 && (
            <>
              <div className="border-bottom p-3 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <i className="bi bi-bookmark d-flex icon icon-sm bg-purple bg-opacity-10 text-purple small"></i>
                  <span className="ms-2 text-secondary text-uppercase fw-semibold small">
                    {t("bookmarks.title")}
                  </span>
                </div>
                <Badge bg="secondary" className="d-flex align-items-center">
                  {results.bookmarks.length}
                </Badge>
              </div>
              <div className="bookmark-list position-relative">
                {results.bookmarks.slice(0, 5).map((bookmark) => (
                  <div
                    onClick={() => openBookmark(bookmark)}
                    className={`bookmark-list__item p-3 cursor-pointer border-bottom`}
                    key={bookmark.uuid}
                  >
                    <div className="d-flex align-items-center">
                      <div className="d-flex justify-content-center align-items-center me-3">
                        <Icon
                          title={bookmark.title}
                          url={bookmark.url}
                          isEncrypted={bookmark.encrypted}
                        />
                      </div>
                      <div className="text-truncate flex-grow-1 me-3">
                        <Title bookmark={bookmark} highlight={searchInput} />
                        <div className="d-block d-md-flex align-items-center">
                          {bookmark?.collections?.length > 0 && (
                            <div className="d-flex overflow-y-auto pe-2 gap-1 bg-white z-2">
                              {bookmark.collections.map((collection) => (
                                <CollectionBadge
                                  key={collection.uuid}
                                  collection={collection}
                                />
                              ))}
                            </div>
                          )}
                          <Url bookmark={bookmark} />
                        </div>
                      </div>
                      <div className="d-flex align-items-center justify-content-end ms-auto bookmark-actions gap-1">
                        {bookmark.favorite && (
                          <i className="bi bi-star-fill text-warning"></i>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {results.bookmarks.length > 5 && (
                  <div className="bg-light py-2 px-3 text-center d-flex align-items-center justify-content-center">
                    <Button
                      variant="none"
                      onClick={() => handleExampleClick(searchInput)}
                      className="text-center text-secondary d-flex align-items-center"
                    >
                      <span>
                        {t("search.modal.results.viewAll", {
                          total: results.pagination.total,
                        })}
                      </span>
                      <i className="bi bi-arrow-right d-flex ms-1"></i>
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Show collections if there are any */}
          {searchInput && !isSearching && results?.collections?.length > 0 && (
            <>
              <div className="border-bottom p-3 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <i className="bi bi-collection d-flex icon icon-sm bg-purple bg-opacity-10 text-purple small"></i>
                  <span className="ms-2 text-secondary text-uppercase fw-semibold small">
                    {t("collections.title")}
                  </span>
                </div>
                <Badge bg="secondary" className="d-flex align-items-center">
                  {results.collections.length}
                </Badge>
              </div>
              <div className="collection-list row g-2 position-relative align-items-stretch m-2 mb-4">
                {results.collections.map((collection) => (
                  <div
                    key={collection.uuid}
                    className={`col-xl-3 col-lg-4 col-md-6 col-sm-12 mb-1`}
                  >
                    <div
                      className="collection-list__item card h-100"
                      style={{ "--bs-collection-color": collection.color }}
                    >
                      <div className="d-flex align-items-center">
                        <i
                          className="rounded-circle ms-1 me-2 p-1"
                          style={{
                            backgroundColor: collection.color,
                          }}
                        ></i>
                        <h5 className="card-title h5 mb-0">
                          {collection.name}
                        </h5>
                      </div>
                      {collection.description && (
                        <p className="ms-1 small text-muted mt-1 mb-2">
                          {collection.description}
                        </p>
                      )}
                      <Link
                        to={`/collections/${collection.uuid}`}
                        className="stretched-link"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* No results message */}
          {searchInput &&
            !isSearching &&
            results?.bookmarks?.length === 0 &&
            results?.collections?.length === 0 &&
            results?.tags?.length === 0 && (
              <div className="no-results text-center p-4">
                <i className="bi bi-search fs-1 text-muted"></i>
                <p className="mt-2">
                  {t("search.modal.noResults.description", {
                    query: searchInput,
                  })}
                </p>
                <button
                  className="btn btn-white btn-sm"
                  onClick={() => handleExampleClick(searchInput)}
                >
                  {t("search.modal.results.searchAnyway", {
                    query: searchInput,
                  })}
                </button>
              </div>
            )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default SearchModal;
